// Pure geometry helper for the half-radial GaugeCard.
//
// Kept separate from the React component so the math is unit-testable without
// a jsdom/RTL test setup.  The component imports `gaugeGeometry` and renders
// the returned paths/coords directly.
//
// Coordinate system:
//   - viewBox is `0 0 GAUGE_VIEWBOX.width GAUGE_VIEWBOX.height` (default 192×110).
//   - The arc baseline sits at y = cy near the bottom edge.
//   - The arc bulges UPWARD (toward y = 0).
//   - SVG y grows downward, so a point "above" the baseline has y < cy.
//
// Bug history: an earlier version used `y = cy + r·sin(angle)` paired with the
// wrong arc sweep flag so the dial fell below the viewBox bottom and the value
// dot rendered offscreen — the "weird circle" Aggregate ROI defect.  Fix
// flipped the y-axis (`cy - r·sin`) AND set sweep-flag = 1, which in SVG's
// y-down userspace traverses angles 180° → 270° → 360° and arcs over the top.
// See docs/implementation/20260515-073810-CODEX-BriefingOpenAndGaugeFix.md.

export const GAUGE_VIEWBOX = { width: 192, height: 110 };
export const GAUGE_DEFAULTS = { cx: 96, cy: 100, r: 78 };

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

export function gaugeGeometry({
  value,
  min = 0,
  max = 100,
  cx = GAUGE_DEFAULTS.cx,
  cy = GAUGE_DEFAULTS.cy,
  r = GAUGE_DEFAULTS.r,
} = {}) {
  const range = max - min || 1;
  const clamped = clamp(value ?? min, min, max);
  const pct = (clamped - min) / range;
  // angle sweeps from π (left baseline) → 0 (right baseline) as pct goes 0 → 1.
  const angle = Math.PI - Math.PI * pct;
  const x = cx + r * Math.cos(angle);
  const y = cy - r * Math.sin(angle); // minus: y grows downward, sin>0 means above baseline.
  const sx = cx - r;
  const sy = cy;
  // sweep flag 0 → arc bulges UP (counterclockwise in SVG screen coords).
  // A half-radial gauge never exceeds 180° so the large-arc flag is always 0.
  // SVG arc sweep semantics with y-axis pointing DOWN:
  //   sweep-flag 1 = arc traverses increasing angle direction (cos θ, sin θ
  //   with sin = screen-y).  For a horizontal chord from (cx-r, cy) to
  //   (cx+r, cy), increasing angle from 180° through 270° to 360° produces
  //   an arc whose midpoint sits at (cx, cy-r) — i.e. ABOVE the baseline.
  //   sweep-flag 0 would arc through 90° (below baseline).
  // A half-radial gauge never exceeds 180° so the large-arc flag is always 0.
  const trackPath = `M ${sx} ${sy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  const filledPath = `M ${sx} ${sy} A ${r} ${r} 0 0 1 ${x.toFixed(2)} ${y.toFixed(2)}`;
  return { x, y, pct, angle, cx, cy, r, sx, sy, trackPath, filledPath };
}
